import { useRef, useState } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import {
  useApproveRachelAgentRunMutation,
  useSetRachelChatToolApprovalMutation,
} from '../../../../services/modules/rachelChats/rachelChats.mutations';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../translation/TranslationContext';
import { useRachelChatContext } from '../../contexts/Rachel.context';
import { usePendingAgentApproval } from '../../hooks/usePendingAgentApproval';
import { useRachelErrorReporter } from '../../hooks/useRachelErrorReporter';
import { useRachelPresentation } from '../../hooks/useRachelPresentation';
import { useRachelStore } from '../../store/rachel.store';
import { getToolApprovalAction } from '../ToolPart/toolApprovalLabel';
import { DecideAgentApprovalParams } from './rachelAgentApprovalCard.types';

export const useRachelAgentApprovalCard = () => {
  const { translate } = useTranslation();
  const { team } = useApplicationContext();
  const { sendMessage, isStreaming } = useRachelChatContext();
  const { isMaximized } = useRachelPresentation();
  const { reportRachelError } = useRachelErrorReporter();
  const pendingApproval = usePendingAgentApproval();
  const activeChatId = useRachelStore((state) =>
    state.activeChatTeamId === team?.id ? state.activeChatId : null,
  );
  const { mutateAsync: approveAgentRun } = useApproveRachelAgentRunMutation();
  const { mutateAsync: setToolApproval } = useSetRachelChatToolApprovalMutation();
  const [isDeciding, setIsDeciding] = useState(false);
  const decidingRef = useRef(false);

  const applyDecision = async ({ approved, consent }: DecideAgentApprovalParams) => {
    if (!pendingApproval || !activeChatId) {
      throw new Error('agent approval requested without an active chat');
    }

    await approveAgentRun({
      chatId: activeChatId,
      approvalId: pendingApproval.approvalId,
      approved,
    });
    if (consent) {
      await setToolApproval({ chatId: activeChatId, ...consent });
    }

    const continuation = approved
      ? LangEnum.RACHEL_APPROVAL_CONTINUE_MESSAGE
      : LangEnum.RACHEL_APPROVAL_DECLINE_MESSAGE;
    sendMessage(translate(continuation));
  };

  const isDisabled = isStreaming || isDeciding;

  const decide = (params: DecideAgentApprovalParams) => {
    if (decidingRef.current || isStreaming) {
      return;
    }
    decidingRef.current = true;
    setIsDeciding(true);
    applyDecision(params)
      .catch((error: unknown) => reportRachelError(error))
      .finally(() => {
        decidingRef.current = false;
        setIsDeciding(false);
      });
  };

  const toolName = pendingApproval?.toolName ?? '';
  const approvalAction = getToolApprovalAction(toolName, pendingApproval?.input);

  return {
    isPending: pendingApproval !== null,
    isDisabled,
    isExpanded: isMaximized,
    approvalPrompt: translate(LangEnum.RACHEL_APPROVAL_PROMPT, {
      action: translate(approvalAction.labelKey, approvalAction.values),
    }),
    handleAllow: () => decide({ approved: true }),
    handleAllowAlways: () => decide({ approved: true, consent: { toolName } }),
    handleAllowAllChat: () => decide({ approved: true, consent: { yoloMode: true } }),
    handleDecline: () => decide({ approved: false }),
  };
};
